/**
 * The chat event vocabulary — AG-UI's, narrowed to what ASAB actually emits.
 *
 * Types and schemas come from `@ag-ui/core`; nothing here redeclares them. The
 * transports map their native wire formats onto this union, so a component can
 * never tell an A2A run from an OpenAI completion except through
 * `ChatCapabilities`.
 *
 * ASAB emits 15 of AG-UI's ~34 event types. State/activity snapshots, deltas,
 * chunked variants, and encrypted reasoning values have no ASAB producer; they
 * are omitted rather than stubbed so an unhandled case is a compile error.
 */
import type {
  Interrupt,
  RunErrorEvent,
  RunFinishedEvent,
  RunStartedEvent,
  ReasoningMessageContentEvent,
  ReasoningMessageEndEvent,
  ReasoningMessageStartEvent,
  StepFinishedEvent,
  StepStartedEvent,
  TextMessageContentEvent,
  TextMessageEndEvent,
  TextMessageStartEvent,
  ToolCallArgsEvent,
  ToolCallEndEvent,
  ToolCallResultEvent,
  ToolCallStartEvent,
} from '@ag-ui/core';
import { EventType } from '@ag-ui/core';

export { EventType };
export type { Interrupt };

/**
 * The subset of AG-UI's event union the ASAB transports emit.
 *
 * Reasoning uses `REASONING_MESSAGE_*`, not `THINKING_TEXT_MESSAGE_*`: the
 * latter is deprecated in @ag-ui/core and slated for removal in 1.0.0, and it
 * carries no `messageId`, so a thought could not be correlated to the message it
 * belongs to.
 */
export type ChatEvent =
  | TextMessageStartEvent
  | TextMessageContentEvent
  | TextMessageEndEvent
  | ReasoningMessageStartEvent
  | ReasoningMessageContentEvent
  | ReasoningMessageEndEvent
  | ToolCallStartEvent
  | ToolCallArgsEvent
  | ToolCallEndEvent
  | ToolCallResultEvent
  | StepStartedEvent
  | StepFinishedEvent
  | RunStartedEvent
  | RunFinishedEvent
  | RunErrorEvent;

/** Every `EventType` this union admits — the runtime mirror of `ChatEvent`. */
export const CHAT_EVENT_TYPES = [
  EventType.TEXT_MESSAGE_START,
  EventType.TEXT_MESSAGE_CONTENT,
  EventType.TEXT_MESSAGE_END,
  EventType.REASONING_MESSAGE_START,
  EventType.REASONING_MESSAGE_CONTENT,
  EventType.REASONING_MESSAGE_END,
  EventType.TOOL_CALL_START,
  EventType.TOOL_CALL_ARGS,
  EventType.TOOL_CALL_END,
  EventType.TOOL_CALL_RESULT,
  EventType.STEP_STARTED,
  EventType.STEP_FINISHED,
  EventType.RUN_STARTED,
  EventType.RUN_FINISHED,
  EventType.RUN_ERROR,
] as const satisfies readonly ChatEvent['type'][];

/**
 * How a cancelled run is encoded.
 *
 * AG-UI has no cancelled run outcome — `RunFinishedOutcomeSchema` is a
 * discriminated union of `success | interrupt` only, and `cancelled` appears
 * nowhere else in the protocol except `ResumeEntry.status`. A user-initiated
 * cancel is not an error, so routing it through `RUN_ERROR` would be a lie the
 * UI then has to un-tell. Instead it rides on `RunFinishedEvent.result`, which
 * the schema types as `z.any()` — in-schema, and narrow enough to be the only
 * convention this module invents.
 */
export const CANCELLED_RESULT = { cancelled: true } as const;

export function isCancelledRun(event: ChatEvent): boolean {
  return (
    event.type === EventType.RUN_FINISHED &&
    (event as RunFinishedEvent).result?.cancelled === true
  );
}

/** The interrupts on a paused run, or `[]` if the run did not pause. */
export function runInterrupts(event: ChatEvent): Interrupt[] {
  if (event.type !== EventType.RUN_FINISHED) return [];
  const outcome = (event as RunFinishedEvent).outcome;
  return outcome?.type === 'interrupt' ? outcome.interrupts : [];
}

/**
 * Why a run was interrupted. ASAB pauses for two distinct reasons that resolve
 * through different endpoints, and `Interrupt.reason` is how they are told apart.
 */
export const INTERRUPT_REASON_TOOL_APPROVAL = 'tool_approval';
export const INTERRUPT_REASON_TOOL_EXECUTION = 'tool_execution';
